import { useEffect, useState } from "react";
import { useAuth } from "context/AuthContext";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import { getUserRole, getUserGroups } from "config/cognito";

/**
 * AuthDebug Component
 *
 * Development tool to debug Cognito authentication.
 * Shows current auth state, user info, tokens, and role information.
 *
 * Usage: Add <AuthDebug /> to any page during development
 * Remove before production!
 */
function AuthDebug() {
  const auth = useAuth();
  const [showDetails, setShowDetails] = useState(false);
  const [tokenPreview, setTokenPreview] = useState(null);

  useEffect(() => {
    if (auth.user?.id_token) {
      try {
        // Decode JWT token (just for preview, don't use this in production)
        const base64Url = auth.user.id_token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join("")
        );
        setTokenPreview(JSON.parse(jsonPayload));
      } catch (e) {
        console.error("Error decoding token:", e);
      }
    } else if (auth.user?.profile) {
      // If using profile directly
      setTokenPreview(auth.user.profile);
    }
  }, [auth.user]);

  // Only show in development
  if (process.env.NODE_ENV === "production") {
    return null;
  }

  return (
    <SoftBox
      position="fixed"
      bottom="20px"
      left="20px"
      bgColor="white"
      borderRadius="lg"
      shadow="lg"
      p={2}
      maxWidth="400px"
      zIndex={9999}
      sx={{ border: "2px solid", borderColor: "info.main" }}
    >
      <SoftBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <SoftTypography variant="h6" color="info" fontWeight="bold">
          🔍 Auth Debug
        </SoftTypography>
        <SoftButton
          size="small"
          variant="outlined"
          onClick={() => setShowDetails(!showDetails)}
        >
          {showDetails ? "Hide" : "Show"} Details
        </SoftButton>
      </SoftBox>

      <SoftBox>
        <SoftTypography variant="caption" color="text" fontWeight="medium">
          Status:
        </SoftTypography>
        <SoftTypography
          variant="body2"
          color={
            auth.isLoading
              ? "warning"
              : auth.isAuthenticated
              ? "success"
              : "error"
          }
          fontWeight="bold"
        >
          {auth.isLoading
            ? "⏳ Loading..."
            : auth.isAuthenticated
            ? "✅ Authenticated"
            : "❌ Not Authenticated"}
        </SoftTypography>
      </SoftBox>

      {auth.error && (
        <SoftBox mt={1}>
          <SoftTypography variant="caption" color="error" fontWeight="medium">
            Error:
          </SoftTypography>
          <SoftTypography variant="body2" color="error">
            {auth.error.message}
          </SoftTypography>
        </SoftBox>
      )}

      {auth.isAuthenticated && auth.user && (
        <>
          <SoftBox mt={1}>
            <SoftTypography variant="caption" color="text" fontWeight="medium">
              User Email:
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              {auth.user.profile?.email || "N/A"}
            </SoftTypography>
          </SoftBox>

          <SoftBox mt={1}>
            <SoftTypography variant="caption" color="text" fontWeight="medium">
              Role:
            </SoftTypography>
            <SoftTypography variant="body2" color="success" fontWeight="bold">
              {getUserRole(auth.user)}
            </SoftTypography>
          </SoftBox>

          <SoftBox mt={1}>
            <SoftTypography variant="caption" color="text" fontWeight="medium">
              Groups:
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              {getUserGroups(auth.user).length > 0
                ? getUserGroups(auth.user).join(", ")
                : "None"}
            </SoftTypography>
          </SoftBox>

          {showDetails && (
            <SoftBox mt={2} p={1} bgColor="grey.100" borderRadius="sm">
              <SoftTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                mb={1}
              >
                Token Preview (ID Token):
              </SoftTypography>
              <pre
                style={{
                  fontSize: "10px",
                  overflow: "auto",
                  maxHeight: "200px",
                  margin: 0,
                }}
              >
                {JSON.stringify(tokenPreview, null, 2)}
              </pre>
            </SoftBox>
          )}

          <SoftBox mt={2} display="flex" gap={1}>
            <SoftButton
              size="small"
              variant="outlined"
              color="error"
              onClick={() => {
                auth.signOut();
                setShowDetails(false);
              }}
            >
              Sign Out
            </SoftButton>
            <SoftButton
              size="small"
              variant="outlined"
              color="info"
              onClick={() => {
                console.log("Auth State:", {
                  isLoading: auth.isLoading,
                  isAuthenticated: auth.isAuthenticated,
                  error: auth.error,
                  user: auth.user,
                  userProfile: auth.user?.profile,
                  role: getUserRole(auth.user),
                  groups: getUserGroups(auth.user),
                });
                alert("Auth state logged to console!");
              }}
            >
              Log to Console
            </SoftButton>
          </SoftBox>
        </>
      )}

      {!auth.isAuthenticated && !auth.isLoading && (
        <SoftBox mt={2}>
          <SoftTypography variant="caption" color="text">
            Go to /sign-in to sign in
          </SoftTypography>
        </SoftBox>
      )}
    </SoftBox>
  );
}

export default AuthDebug;
